import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CATALOG_PRODUCT_LIMIT, TOOLS } from '../constants/tools';
import { useOptionalAgent } from '../ConversationFlow/AgentContext';
import {
  fetchAgentToolStates,
  getCachedAgentToolStates
} from './activeToolsCache';

const limitVars = { limit: CATALOG_PRODUCT_LIMIT };

const mapTool = (tool, t) => ({
  ...tool,
  title: t(tool.nameKey, limitVars),
  description: tool.descriptionKey ? t(tool.descriptionKey, limitVars) : null,
  note: tool.noteKey ? t(tool.noteKey, limitVars) : null
});

/** Group locked/available tools by original icon color family. */
const TOOL_COLOR_ORDER = ['blue', 'green', 'vermilion'];

const getToolColorRank = (tool) => {
  const colorClass = tool.colorClass || '';
  const index = TOOL_COLOR_ORDER.findIndex((color) => colorClass.includes(color));
  return index === -1 ? TOOL_COLOR_ORDER.length : index;
};

const catalogIndexById = new Map(TOOLS.map((tool, index) => [tool.id, index]));

const compareToolsByColor = (a, b) => {
  const lockDiff = Number(a.isLocked) - Number(b.isLocked);
  if (lockDiff !== 0) return lockDiff;

  const colorDiff = getToolColorRank(a) - getToolColorRank(b);
  if (colorDiff !== 0) return colorDiff;

  return (catalogIndexById.get(a.id) ?? 0) - (catalogIndexById.get(b.id) ?? 0);
};

export const useTools = () => {
  const { t } = useTranslation(['ChatbotIA', 'Common']);
  const { agentId } = useOptionalAgent() || {};
  const [toolStates, setToolStates] = useState(
    () => (agentId ? getCachedAgentToolStates(agentId) ?? null : null)
  );

  useEffect(() => {
    if (!agentId) {
      setToolStates(null);
      return undefined;
    }

    const cached = getCachedAgentToolStates(agentId);
    if (cached !== undefined) {
      setToolStates(cached);
      return undefined;
    }

    let cancelled = false;

    fetchAgentToolStates(agentId).then((states) => {
      if (!cancelled) setToolStates(states);
    });

    return () => {
      cancelled = true;
    };
  }, [agentId]);

  const catalogTools = useMemo(
    () => TOOLS.map((tool) => mapTool(tool, t)),
    [t]
  );

  const tools = useMemo(() => {
    if (!toolStates) return [];

    const stateByType = new Map(
      toolStates.map((state) => [state.type, state])
    );

    return catalogTools
      .filter((tool) => stateByType.has(tool.id))
      .map((tool) => {
        const state = stateByType.get(tool.id);
        const isAvailable = state?.active === true;
        const requiresSubscription = state?.requiresSubscription === true;
        return {
          ...tool,
          isAvailable,
          requiresSubscription,
          isLocked: requiresSubscription || !isAvailable
        };
      })
      .sort(compareToolsByColor);
  }, [toolStates, catalogTools]);

  const getToolDetails = (id) => catalogTools.find((tool) => tool.id === id) || null;

  const isLoading = Boolean(agentId) && toolStates === null;

  return {
    tools,
    getToolDetails,
    isLoading
  };
};

export default useTools;
